/* import __COLOCATED_TEMPLATE__ from './sh-invoices-record-list-item.hbs'; */
import { A } from '@ember/array';
import { computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { EMBER_ROUTE_NAMES } from "../../../../constants/routes";
import { SH_INVOICE } from "../../../../models/sh-invoice";

/**
 * This is an individual record item for the sh-invoice list view in admin
 */
export default Component.extend({
  /**
   * @inheritDoc
   */
  tagName: 'li',
  classNames: ['SH-c-invoices-record-list-item', 'LSG-l-grid', 'justify-between', 'padding-top-bottom-16px', 'border-top-lsg-black'],
  classNameBindings: [
    '_isOddIndex:background-lsg-xlight-gray',
    '_isOddIndex::background-lsg-white'
  ],

  /**
   * Register services
   */
  state: service('states/routes/admin/accounting/payin'),
  stateManager: service('state-managers/routes/admin/accounting/payin'),
  moment: service(),

  /*********************************
   * REQUIRED PARAMS
   *********************************/

  /**
   * @param {Number}
   */
  indexInTable: 0,

  /**
   * @param {ShInvoice}
   */
  shInvoice: null,

  /*********************************
   * OPTIONAL PARAMS
   **********************************/

  /*********************************
   * LIFECYCLE FUNCTIONS
   **********************************/

  /*********************************
   * PRIVATE
   **********************************/

  /**
   * @type {Object}
   */
  _constantsRouteNames: EMBER_ROUTE_NAMES,

  /**
   * @type {Company|null}
   */
  _companyEntity: computed('shInvoice.company', function() {
    return this.get('shInvoice.company');
  }),

  /**
   * @type {String}
   */
  _costCodes: computed('shInvoice.childShInvoices.[]', function() {
    let costCodes = A();
    if (!this.get('shInvoice.childShInvoice.length')) {
      let lineItemWithApplication = this.get('shInvoice.shInvoiceLineItems').find(shInvoiceLineItem => {
        return shInvoiceLineItem.get('application.id');
      });

      return lineItemWithApplication?.application?.costCode?.body ?? '';
    }

    this.get('shInvoice.childShInvoices').forEach(childShInvoice => {
      let lineItemWithApplication = childShInvoice.get('shInvoiceLineItems').find(childShInvoiceLineItem => {
        return childShInvoiceLineItem.get('application.id');
      });

      const costCodeBody = lineItemWithApplication?.application?.costCode?.body;
      if (costCodeBody) {
        costCodes.pushObject(costCodeBody);
      }
    });

    return costCodes.join(' || ');
  }),

  /**
   * @type {Number}
   */
  _invoiceTotalAmount: computed(
  'shInvoice.amount',
  'shInvoice.shInvoiceLineItems.@each.amount',
  'shInvoice.isBatchedInvoice',
  function() {
    if (this.get('shInvoice.isBatchedInvoice')) {
      return this.get('shInvoice.amount');
    }

    return this.get('shInvoice.shInvoiceLineItems').reduce((carry, shInvoiceLineItem) => {
      return carry + shInvoiceLineItem.get('amount');
    }, 0);
  }),

  /**
   * @type {Boolean}    If true this means this invoice has been selected in the batched invoice experience.
   */
  _isChecked: computed('state.batchedInvoicesSelected.[]', function() {
    return this.get('state.batchedInvoicesSelected').includes(this.get('shInvoice.id'));
  }),

  /**
   * @type {Boolean}
   */
  _isOddIndex: computed('indexInTable', function() {
    return this.get('indexInTable') % 2 === 1;
  }),

  /**
   * Get a 'paid' pseudo status class of the ShInvoice
   *
   * @type {String}     class for the text
   */
  _statusPaidTextClass: computed(
  'shInvoice.statusPaidText',
  function() {
    let statusClass = 'font-weight-700';

    switch (this.get('shInvoice.statusPaidText')) {
      case SH_INVOICE.PAID_STATUS_TEXT.PARTIALLY_PAID:
        return statusClass + ' lsg-yellow';
      case SH_INVOICE.PAID_STATUS_TEXT.PAID_EXACTLY:
        return statusClass + ' lsg-green';
      case SH_INVOICE.PAID_STATUS_TEXT.OVERPAID:
        return statusClass + ' lsg-green';
      case SH_INVOICE.PAID_STATUS_TEXT.UNPAID:
        return statusClass + ' lsg-yellow';
    }

    return statusClass + ' lsg-dark-gray';
  }),

  dueAtText: computed(
    'shInvoice.dueAt',
    function() {
      if (this.get('shInvoice.dueAt')) {
        return this.get('moment').moment(this.get('shInvoice.dueAt')).format('YYYY-MM-DD');
      }

      return '';
    }
  ),

  finalAtText: computed(
    'shInvoice.finalAt',
    function() {
      const finalAt = this.get('shInvoice.finalAt');
      if (finalAt) {
        return this.get('moment').moment(finalAt).format('YYYY-MM-DD');
      }

      return '';
    }
  ),

  revisedAtText: computed(
    'shInvoice.revisedAt',
    function() {
      if (this.get('shInvoice.revisedAt')) {
        return this.get('moment').moment(this.get('shInvoice.revisedAt')).format('YYYY-MM-DD');
      }

      return '';
    }
  ),

  lastSentAtText: computed(
    'shInvoice.lastSentAt',
    function() {
      if (this.get('shInvoice.lastSentAt')) {
        return this.get('moment').moment(this.get('shInvoice.lastSentAt')).format('YYYY-MM-DD');
      }

      return '';
    }
  ),

  actions: {
    /**
     * This has been selected for a batch.
     */
    onCheckboxClicked() {
      this.stateManager.toggleBatchedCheckboxSelected(this.shInvoice);
    }
  }
});
